// ============================================================
//  src/pages/MetaVerificationWizard.js
//  Step-by-step guided wizard for getting a verified Meta
//  Developer account + WhatsApp Business API access.
//
//  This does NOT automate Meta's verification (impossible —
//  it's deliberately a manual, identity-bound process). It
//  guides the user through every screen and tracks their
//  progress locally so they can pick up where they left off.
// ============================================================
import React, { useState, useEffect } from 'react';

const STORAGE_KEY = 'metaWizardProgress';

const STEPS = [
  {
    id: 'facebook-account',
    title: 'Create a Facebook account',
    time: '2 min',
    body: (
      <>
        <p>Meta requires a real personal Facebook account behind every developer account — there's no way around this, and no API can create one on your behalf (it's against Meta's own terms).</p>
        <p>If you already have a personal Facebook account, skip this step. Otherwise:</p>
      </>
    ),
    action: { label: 'Go to facebook.com/signup', url: 'https://www.facebook.com/signup' },
    tip: 'Use an account you (or your business) will keep long-term. Switching the underlying Facebook account later is painful — Meta ties your whole developer identity to it.',
  },
  {
    id: 'dev-account',
    title: 'Register as a Meta Developer',
    time: '2 min',
    body: (
      <>
        <p>This turns your Facebook account into a developer account, giving you access to the App Dashboard.</p>
        <ol>
          <li>Go to the Meta for Developers site and click <strong>Get Started</strong></li>
          <li>Accept the Developer Policies</li>
          <li>Confirm the account type when asked (choose <strong>Business</strong> if you're building this for clients, not just yourself)</li>
        </ol>
      </>
    ),
    action: { label: 'Go to developers.facebook.com', url: 'https://developers.facebook.com/' },
  },
  {
    id: 'security',
    title: 'Verify your identity (2FA + phone)',
    time: '3 min',
    body: (
      <>
        <p>Meta requires two-factor authentication and a verified phone number (sometimes a payment method too) before your account is treated as trustworthy.</p>
        <ul>
          <li>Enable 2FA — authenticator app (Google Authenticator, etc.) is faster than SMS</li>
          <li>Confirm your phone number via the OTP Meta sends</li>
          <li>If prompted, add a payment method — Meta sometimes charges a small refundable amount purely to confirm the card is real</li>
        </ul>
      </>
    ),
    warning: 'Use a phone number and card you control long-term. These get tied to account recovery — losing access to both can lock you out permanently.',
  },
  {
    id: 'business-manager',
    title: 'Create a Business Manager account',
    time: '5 min',
    body: (
      <>
        <p>This is where your WhatsApp number, ad accounts, and team access all live. You need one even if you're a solo developer.</p>
        <ol>
          <li>Go to Meta Business Suite / Business Manager</li>
          <li>Click <strong>Create Account</strong></li>
          <li>Enter your real business name, your name, and a business email (not a free Gmail/Yahoo address if possible — it helps verification later)</li>
        </ol>
      </>
    ),
    action: { label: 'Go to business.facebook.com', url: 'https://business.facebook.com/' },
  },
  {
    id: 'business-verification',
    title: 'Verify your business',
    time: '1–5 business days',
    body: (
      <>
        <p>This is the step that actually unlocks the permissions you need for WhatsApp messaging at scale. Meta confirms your business is a real legal entity.</p>
        <ol>
          <li>In Business Manager → Business Settings → <strong>Security Center</strong> → <strong>Start Verification</strong></li>
          <li>Enter your legal business name, address, and registration number exactly as they appear on your official documents</li>
          <li>Upload a business document — registration certificate, tax ID document, or utility bill matching the business address</li>
          <li>Confirm a phone number or email at your business domain, if asked</li>
        </ol>
      </>
    ),
    warning: 'Mismatches are the #1 cause of rejection. Your business name, address, and document must match exactly — even small punctuation differences can trigger a manual review or rejection.',
  },
  {
    id: 'create-app',
    title: 'Create your app',
    time: '3 min',
    body: (
      <>
        <p>The "app" is your project's identity inside Meta's system — it's what gets permissions, tokens, and review approvals.</p>
        <ol>
          <li>App Dashboard → <strong>Create App</strong></li>
          <li>Choose use case: <strong>Other</strong> → <strong>Business</strong> (this unlocks WhatsApp/Messenger products)</li>
          <li>Link it to the Business Manager account you created in step 4</li>
          <li>Give it a clear name — this is what reviewers and your future self will see</li>
        </ol>
      </>
    ),
    action: { label: 'Go to App Dashboard', url: 'https://developers.facebook.com/apps/' },
  },
  {
    id: 'add-whatsapp',
    title: 'Add the WhatsApp product',
    time: '5 min',
    body: (
      <>
        <p>From your App Dashboard → <strong>Add Product</strong> → <strong>WhatsApp</strong> → <strong>Set up</strong>.</p>
        <p>This immediately gives you a free test number and a temporary 24-hour access token — enough to send test messages right away, before any review is needed.</p>
      </>
    ),
    tip: 'Use the test number first to confirm your integration works end-to-end before requesting a real business number or submitting anything for review.',
  },
  {
    id: 'permanent-token',
    title: 'Generate a permanent access token',
    time: '5 min',
    body: (
      <>
        <p>The default token expires in 24 hours — not usable in production. Create a System User instead:</p>
        <ol>
          <li>Business Settings → <strong>System Users</strong> → <strong>Add</strong></li>
          <li>Assign it <strong>Admin</strong> role on your app and WhatsApp asset</li>
          <li>Generate a token for that System User, scoped to <code>whatsapp_business_messaging</code> and <code>whatsapp_business_management</code></li>
        </ol>
      </>
    ),
    warning: 'Store this token like a password. Anyone with it can send messages as your business number.',
  },
  {
    id: 'app-review',
    title: 'Submit for App Review (production access)',
    time: '1–7 business days',
    body: (
      <>
        <p>Required only once you want to message numbers outside your small list of pre-approved test recipients, or need permissions beyond the test-mode defaults.</p>
        <ol>
          <li>App Dashboard → <strong>App Review</strong> → <strong>Permissions and Features</strong></li>
          <li>Request <code>whatsapp_business_messaging</code></li>
          <li>Provide a clear use-case description and, if asked, a short screen recording showing the messaging flow in action</li>
          <li>Submit, then check back on this app's App Review tab for the decision</li>
        </ol>
      </>
    ),
  },
  {
    id: 'go-live',
    title: "Switch your app to Live",
    time: '1 min',
    body: (
      <>
        <p>Development apps only work for added test users. Once your review is approved:</p>
        <ol>
          <li>App Dashboard → top toggle → switch from <strong>In development</strong> to <strong>Live</strong></li>
          <li>Confirm your Privacy Policy URL and Data Use Checkup are filled in — Meta blocks Live mode without these</li>
        </ol>
      </>
    ),
  },
];

// ── tiny inline icon set, no extra deps ─────────────────────
function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
      <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function ExternalIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
      <path d="M7 17 17 7M9 7h8v8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function ClockIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
      <path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function InfoBox({ type = 'info', children }) {
  const styles = {
    info:    { bg: 'rgba(0,44,237,0.06)',  border: 'rgba(0,44,237,0.2)',  icon: 'ℹ️' },
    warning: { bg: 'rgba(255,149,0,0.08)', border: 'rgba(255,149,0,0.3)', icon: '⚠️' },
    tip:     { bg: 'rgba(0,122,255,0.06)', border: 'rgba(0,122,255,0.2)', icon: '💡' },
  };
  const s = styles[type] ?? styles.info;
  return (
    <div style={{
      background: s.bg, border: `1px solid ${s.border}`, borderRadius: 10,
      padding: '10px 14px', marginTop: 12, display: 'flex', gap: 10,
      fontSize: 13.5, lineHeight: 1.6, color: 'var(--text-mid)',
    }}>
      <span style={{ flexShrink: 0 }}>{s.icon}</span>
      <div style={{ flex: 1 }}>{children}</div>
    </div>
  );
}

function WizardStep({ step, index, total, done, onToggleDone }) {
  return (
    <div style={{
      borderRadius: 14, border: `1px solid ${done ? 'rgba(0,196,140,0.3)' : 'var(--border, #e5e7eb)'}`,
      background: done ? 'rgba(0,196,140,0.04)' : 'var(--bg-card, #fff)',
      padding: 'clamp(16px, 4vw, 22px)', marginBottom: 16,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
        <button
          onClick={() => onToggleDone(step.id)}
          aria-label={done ? 'Mark as not done' : 'Mark as done'}
          style={{
            flexShrink: 0, width: 30, height: 30, borderRadius: '50%',
            border: done ? 'none' : '2px solid var(--border, #d1d5db)',
            background: done ? 'var(--success, #00c48c)' : 'transparent',
            color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', marginTop: 2,
          }}
        >
          {done ? <CheckIcon /> : <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--text-muted)' }}>{index + 1}</span>}
        </button>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
            <div style={{ fontWeight: 800, fontSize: 'clamp(14px, 4vw, 15.5px)', color: 'var(--text-dark)' }}>
              Step {index + 1} of {total} — {step.title}
            </div>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11.5,
              padding: '2px 8px', borderRadius: 999, background: 'rgba(0,0,0,0.05)', color: 'var(--text-muted)',
            }}>
              <ClockIcon /> {step.time}
            </span>
          </div>

          <div style={{ fontSize: 13.5, color: 'var(--text-mid)', lineHeight: 1.7 }}>
            {step.body}
          </div>

          {step.action && (
            <a
              href={step.action.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 10,
                fontSize: 13, fontWeight: 700, color: 'var(--primary)', textDecoration: 'none',
              }}
            >
              {step.action.label} <ExternalIcon />
            </a>
          )}

          {step.tip && <InfoBox type="tip">{step.tip}</InfoBox>}
          {step.warning && <InfoBox type="warning">{step.warning}</InfoBox>}
        </div>
      </div>
    </div>
  );
}

export default function MetaVerificationWizard() {
  const [doneIds, setDoneIds] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) ?? [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(doneIds));
  }, [doneIds]);

  function toggleDone(id) {
    setDoneIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  }

  const progress = Math.round((doneIds.length / STEPS.length) * 100);

  return (
    <div style={{ maxWidth: 760, margin: '0 auto', padding: 'clamp(16px, 5vw, 32px)' }}>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 'clamp(20px, 5vw, 26px)', fontWeight: 800, color: 'var(--text-dark)', margin: 0 }}>
          Get a verified Meta Developer account
        </h1>
        <p style={{ fontSize: 14, color: 'var(--text-mid)', marginTop: 8, lineHeight: 1.7 }}>
          A step-by-step walkthrough to get your WhatsApp Business API access fully verified and production-ready. This can't be fully automated — Meta deliberately keeps identity verification manual — but we'll guide you through every screen and track your live status where possible.
        </p>

        <div style={{ marginTop: 18 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 6 }}>
            <span>{doneIds.length} of {STEPS.length} steps complete</span>
            <span>{progress}%</span>
          </div>
          <div style={{ height: 8, borderRadius: 999, background: 'rgba(0,0,0,0.06)', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${progress}%`, background: 'var(--primary)', borderRadius: 999, transition: 'width 0.3s' }} />
          </div>
        </div>
      </div>

      {STEPS.map((step, i) => (
        <WizardStep
          key={step.id}
          step={step}
          index={i}
          total={STEPS.length}
          done={doneIds.includes(step.id)}
          onToggleDone={toggleDone}
        />
      ))}

      {progress === 100 && (
        <div style={{
          textAlign: 'center', padding: 24, borderRadius: 14,
          background: 'var(--primary-light, rgba(0,44,237,0.06))', marginTop: 8,
        }}>
          <div style={{ fontSize: 22, marginBottom: 6 }}>🎉</div>
          <div style={{ fontWeight: 800, color: 'var(--primary)' }}>You're fully verified and live.</div>
          <div style={{ fontSize: 13, color: 'var(--text-mid)', marginTop: 4 }}>
            Head back to your bot's Overview tab to connect your verified number.
          </div>
        </div>
      )}
    </div>
  );
}